import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { Bell, CheckCheck } from 'lucide-react'
import { getNotifications, getUnreadCount, NOTIFICATION_KEYS, type NotificationFilter } from '../../api/notifications'
import { useDismissOnOutside } from '../../lib/dropdown'
import { formatNumber, plural } from '../../lib/format'
import { Modal, Segmented, Spinner } from '../ui'
import { NotificationItem } from './NotificationItem'
import { useNotificationActions } from './useNotificationActions'

const PANEL_PAGE = 10
const PANEL_WIDTH = 400

/** Počet nepřečtených — polling každou minutu a při návratu na kartu. Při novém nic nepípá. */
export function useUnreadCount() {
  return useQuery({
    queryKey: NOTIFICATION_KEYS.unread,
    queryFn: getUnreadCount,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: 'always',
    staleTime: 30_000,
    retry: false,
  })
}

/**
 * Zvoneček s odznakem nepřečtených.
 *
 * - `mobile`: v horní liště vedle avataru; panel = celoobrazovkový Modal.
 * - `desktop`: v horní části bočního menu (plné menu i ikonový pruh); panel = rozbalovací panel
 *   pod zvonečkem (portál, aby ho nesvíralo úzké menu), zavírá se ťuknutím mimo / Escape.
 */
export function NotificationBell({ variant }: { variant: 'mobile' | 'desktop' }) {
  const [open, setOpen] = useState(false)
  const { data: unread = 0 } = useUnreadCount()
  const panelRef = useRef<HTMLDivElement>(null)
  const wrapRef = useDismissOnOutside<HTMLDivElement>(open && variant === 'desktop', () => setOpen(false), [panelRef])
  const buttonRef = useRef<HTMLButtonElement>(null)
  const { pathname, search } = useLocation()

  // Po přechodu na jinou stránku panel zavřít.
  useEffect(() => { setOpen(false) }, [pathname, search])

  const badge = unread > 99 ? '99+' : String(unread)
  const label = unread > 0
    ? `Notifikace (${formatNumber(unread)} ${plural(unread, 'nepřečtená', 'nepřečtené', 'nepřečtených')})`
    : 'Notifikace (žádné nepřečtené)'

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="dialog"
        className={clsx(
          'relative inline-flex h-11 w-11 items-center justify-center rounded-lg text-ink-700 hover:bg-slate-100',
          open && 'bg-slate-100',
        )}
      >
        <Bell size={22} />
        {unread > 0 && (
          <span className="absolute right-0.5 top-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-600 px-1 text-xs font-semibold leading-none text-white ring-2 ring-white">
            {badge}
          </span>
        )}
      </button>

      {variant === 'mobile' ? (
        <Modal open={open} onClose={() => setOpen(false)} title="Notifikace" mobileFullscreen size="md" bodyClassName="!px-0 !py-0 flex flex-col">
          <NotificationPanelBody onClose={() => setOpen(false)} />
        </Modal>
      ) : (
        open && <DesktopPopover anchor={buttonRef} panelRef={panelRef} onClose={() => setOpen(false)} />
      )}
    </div>
  )
}

function DesktopPopover({ anchor, panelRef, onClose }: { anchor: React.RefObject<HTMLButtonElement>; panelRef: React.RefObject<HTMLDivElement>; onClose: () => void }) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  useLayoutEffect(() => {
    const place = () => {
      const r = anchor.current?.getBoundingClientRect()
      if (!r) return
      const left = Math.max(8, Math.min(r.left, window.innerWidth - PANEL_WIDTH - 8))
      setPos({ top: r.bottom + 6, left })
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [anchor])

  if (!pos) return null
  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Notifikace"
      className="fixed z-[75] flex animate-fade-in flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-soft-lg"
      style={{ top: pos.top, left: pos.left, width: PANEL_WIDTH, maxWidth: 'calc(100vw - 16px)', maxHeight: `calc(100vh - ${pos.top + 16}px)` }}
    >
      <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-4 py-3">
        <h2 className="text-base font-semibold text-ink-900">Notifikace</h2>
      </div>
      <NotificationPanelBody onClose={onClose} />
    </div>,
    document.body,
  )
}

/** Obsah panelu: filtr, „Označit vše", seznam s „Načíst další" a odkaz na celou stránku. */
function NotificationPanelBody({ onClose }: { onClose: () => void }) {
  const [filter, setFilter] = useState<NotificationFilter>('all')
  const { openNotification, markAll, markingAll } = useNotificationActions(onClose)
  const { data: unread = 0 } = useUnreadCount()

  const query = useInfiniteQuery({
    queryKey: [...NOTIFICATION_KEYS.all, 'panel', filter],
    queryFn: ({ pageParam }) => getNotifications({ page: pageParam, per_page: PANEL_PAGE, filter }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.pagination.page < last.pagination.total_pages ? last.pagination.page + 1 : undefined),
    staleTime: 0,
  })
  const items = query.data?.pages.flatMap((p) => p.items) ?? []
  const total = query.data?.pages[0]?.pagination.total ?? 0
  const remaining = Math.max(0, total - items.length)

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 flex-nowrap items-center justify-between gap-2 px-3 py-2">
        <Segmented
          ariaLabel="Filtr notifikací"
          className="!w-auto whitespace-nowrap"
          options={[
            { value: 'all', label: 'Vše' },
            { value: 'unread', label: unread > 0 ? `Nepřečtené (${unread > 99 ? '99+' : unread})` : 'Nepřečtené' },
          ]}
          value={filter}
          onChange={setFilter}
        />
        <button
          type="button"
          onClick={() => markAll()}
          disabled={unread === 0 || markingAll}
          title="Označit vše jako přečtené"
          aria-label="Označit vše jako přečtené"
          className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:text-ink-400 disabled:hover:bg-transparent"
        >
          <CheckCheck size={16} /> Přečíst vše
        </button>
      </div>

      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pb-2">
        {query.isLoading ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : query.isError ? (
          <p className="px-3 py-8 text-center text-sm text-red-700">Notifikace se nepodařilo načíst.</p>
        ) : items.length === 0 ? (
          <div className="px-3 py-10 text-center">
            <Bell size={28} className="mx-auto text-ink-400" aria-hidden />
            <p className="mt-2 text-sm font-medium text-ink-800">{filter === 'unread' ? 'Žádné nepřečtené notifikace' : 'Zatím žádné notifikace'}</p>
            <p className="mt-1 text-sm text-ink-500">Dáme vědět, až vám někdo naplánuje práci, zadá úkol nebo vyřídí přípravu.</p>
          </div>
        ) : (
          <ul className="space-y-0.5">
            {items.map((n) => (
              <li key={n.id}><NotificationItem notification={n} onOpen={openNotification} /></li>
            ))}
          </ul>
        )}
        {query.hasNextPage && (
          <button
            type="button"
            onClick={() => query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
            className="mt-1 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg text-sm font-medium text-ink-700 hover:bg-slate-100"
          >
            {query.isFetchingNextPage ? <Spinner /> : `Načíst další (zbývá ${formatNumber(remaining)})`}
          </button>
        )}
      </div>

      <div className="shrink-0 border-t border-slate-100 px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        <Link
          to="/notifications"
          onClick={onClose}
          className="flex min-h-[44px] items-center justify-center rounded-lg text-sm font-semibold text-brand-700 hover:bg-brand-50"
        >
          Zobrazit vše{total > 0 ? ` (${formatNumber(total)})` : ''}
        </Link>
      </div>
    </div>
  )
}
